import { useState } from "react";
import { Loader } from "@mantine/core";
import { toast } from "sonner";
import {
  useBlockedCustomersQuery,
  useUnblockCustomerMutation,
} from "../api/queries/customerOrganizationQueries";
import { ConfirmationModal } from "./ConfirmationModal";
import { formatAppDate } from "../utils/date";

export function BlockedCustomersSection() {
  const { data, isLoading, refetch } = useBlockedCustomersQuery();
  const unblockMutation = useUnblockCustomerMutation();
  const [pendingUnblockId, setPendingUnblockId] = useState<number | null>(null);

  const pendingCustomer = data?.items.find((item) => item.id === pendingUnblockId);

  const handleUnblock = async () => {
    if (pendingUnblockId == null) return;
    try {
      await unblockMutation.mutateAsync(pendingUnblockId);
      toast.success("Customer unblocked");
      setPendingUnblockId(null);
      await refetch();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { detail?: { message?: string } } } })
          ?.response?.data?.detail?.message || "Failed to unblock customer";
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader />
      </div>
    );
  }

  const customers = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800">Blocked customers</h2>
        <p className="text-sm text-gray-500">
          Customers blocked from Budy. Unblock to allow data to repopulate on the
          next accounting refresh.
        </p>
      </div>

      {customers.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-sm">
          No blocked customers.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Name
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Accounting ID
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Blocked
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.map((customer) => (
                <tr key={customer.id}>
                  <td className="px-4 py-3 text-gray-900">
                    {customer.customer_organization_name}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {customer.accounting_software_id}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {formatAppDate(customer.blocked_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setPendingUnblockId(customer.id)}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Unblock
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmationModal
        open={pendingUnblockId != null}
        title="Unblock customer?"
        description={
          pendingCustomer
            ? `Unblock ${pendingCustomer.customer_organization_name}? Their contacts and invoices will repopulate on the next accounting refresh.`
            : undefined
        }
        confirmLabel="Unblock"
        confirmTone="primary"
        isLoading={unblockMutation.isPending}
        onCancel={() => setPendingUnblockId(null)}
        onConfirm={handleUnblock}
      />
    </div>
  );
}
